import React, { useState, useEffect } from 'react';
import { SAMPLE_BUSINESSES } from '@/data/businesses';
import PageHeader from '@/components/ui/PageHeader';
import ListingSubmissionModal from '@/components/directory/ListingSubmissionModal';
import { usePageMeta } from '@/hooks/usePageMeta';
import type { BusinessListing, BusinessReview } from '@/types/business';
import './DirectoryPage.css';

function coverageLabel(biz: BusinessListing): string {
  if (biz.contentType !== 'business') return '🌍 Worldwide';
  if (biz.coverageType === 'radius' && biz.coverageTown) {
    return `📍 Within ${biz.coverageRadiusMiles}mi of ${biz.coverageTown}`;
  }
  if (biz.coverageType === 'national') return `🗺️ ${biz.country}`;
  if (biz.coverageType === 'international') return '🌍 International';
  return `📍 ${biz.geographicArea}`;
}

export default function DirectoryPage() {
  usePageMeta(
    'Growers Directory',
    'Find organic nurseries, seed suppliers, garden designers, blogs, vlogs and podcasts — with ratings and reviews from fellow growers.',
  );
  const [listings, setListings] = useState<BusinessListing[]>(SAMPLE_BUSINESSES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessListing | null>(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', body: '' });
  const [showListingModal, setShowListingModal] = useState(false);

  useEffect(() => {
    fetch('/api/businesses')
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const results: BusinessListing[] = data?.listings ?? [];
        // Show real listings if any exist, otherwise fall back to sample data
        setListings(results.length > 0 ? results : SAMPLE_BUSINESSES);
      })
      .catch(() => setListings(SAMPLE_BUSINESSES))
      .finally(() => setLoading(false));
  }, []);

  const filtered = listings.filter(b => {
    if (typeFilter && b.contentType !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !b.name.toLowerCase().includes(q) &&
        !b.geographicArea.toLowerCase().includes(q) &&
        !(b.coverageTown?.toLowerCase().includes(q)) &&
        !b.tagline.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  if (selectedBusiness) {
    return (
      <BusinessDetail
        business={selectedBusiness}
        onBack={() => setSelectedBusiness(null)}
        showReviewForm={showReviewForm}
        setShowReviewForm={setShowReviewForm}
        reviewForm={reviewForm}
        setReviewForm={setReviewForm}
      />
    );
  }

  return (
    <div className="directory-page">
      <PageHeader
        title="Growers Directory"
        subtitle="Find local nurseries, garden designers, and inspiring blogs and vlogs from the growing community."
        eyebrow="The growing community"
        decorationEmoji="🔍"
      />

      <div className="container">
        <div className="directory-filters section-sm">
          <input
            className="input directory-search"
            type="search"
            placeholder="Search by name or area…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="directory-type-filters">
            {['', 'business', 'blog', 'vlog', 'podcast'].map(type => (
              <button
                key={type}
                className={`advice-cat-btn ${typeFilter === type ? 'advice-cat-btn--active' : ''}`}
                onClick={() => setTypeFilter(type)}
              >
                {type === '' ? 'All' : type === 'business' ? '🏪 Businesses' : type === 'blog' ? '✍️ Blogs' : type === 'vlog' ? '📹 Vlogs' : '🎙 Podcasts'}
              </button>
            ))}
          </div>
        </div>

        <div className="directory-cta-banner">
          <div className="directory-cta-text">
            <h3 className="text-title">List your business or channel</h3>
            <p className="text-small" style={{ color: 'var(--ink-light)' }}>Reach organic growers in your area. Nurseries, designers, suppliers, bloggers and more.</p>
          </div>
          <button className="btn-sage" onClick={() => setShowListingModal(true)}>List my business →</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-16) 0' }}>
            <p style={{ fontSize: '2rem', marginBottom: 'var(--space-3)' }}>🌿</p>
            <p className="text-hand" style={{ color: 'var(--sage)' }}>Loading directory…</p>
          </div>
        ) : (
          <div className="directory-grid">
            {filtered.map((biz, i) => (
              <button key={biz.id} className={`directory-card animate-fade-in-delay-${Math.min(i + 1, 5)}`} onClick={() => setSelectedBusiness(biz)}>
                {biz.featured && <span className="directory-featured text-caption">⭐ Featured</span>}
                <div className="directory-card-header">
                  <div className="directory-card-avatar">
                    {biz.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-title" style={{ fontSize: '1.1rem' }}>{biz.name}</h3>
                    <p className="text-caption" style={{ color: 'var(--ink-faint)' }}>{coverageLabel(biz)}</p>
                  </div>
                  <span className={`directory-type-badge ${biz.contentType}`}>
                    {biz.contentType === 'business' ? '🏪' : biz.contentType === 'blog' ? '✍️' : biz.contentType === 'vlog' ? '📹' : '🎙'} {biz.contentType}
                  </span>
                </div>

                <p className="directory-tagline text-hand">{biz.tagline}</p>
                <p className="text-small" style={{ color: 'var(--ink-light)' }}>{biz.description.slice(0, 120)}…</p>

                <div className="directory-card-footer">
                  <div className="directory-rating">
                    <Stars rating={biz.averageRating} />
                    <span className="text-caption">{biz.averageRating.toFixed(1)} ({biz.reviewCount})</span>
                  </div>
                  {biz.organicCertified && <span className="text-caption" style={{ color: 'var(--sage)' }}>🌿 Organic certified</span>}
                  {biz.latestPostDate && <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>Latest: {new Date(biz.latestPostDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 'var(--space-16) 0' }}>
            <p style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>🔍</p>
            <p className="text-subtitle" style={{ color: 'var(--ink-light)' }}>No listings match your search.</p>
          </div>
        )}
      </div>

      {showListingModal && (
        <ListingSubmissionModal onClose={() => setShowListingModal(false)} />
      )}
    </div>
  );
}

function BusinessDetail({ business, onBack, showReviewForm, setShowReviewForm, reviewForm, setReviewForm }: {
  business: BusinessListing;
  onBack: () => void;
  showReviewForm: boolean;
  setShowReviewForm: (v: boolean) => void;
  reviewForm: { rating: number; title: string; body: string };
  setReviewForm: (f: { rating: number; title: string; body: string }) => void;
}) {
  return (
    <div className="directory-detail">
      <div className="container">
        <button className="advice-back text-small" onClick={onBack} style={{ fontFamily: 'var(--font-body)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--sage)', fontWeight: 600, padding: 'var(--space-8) 0 var(--space-4)' }}>
          ← Back to directory
        </button>

        <div className="directory-detail-layout">
          <div className="directory-detail-main">
            <div className="paper directory-detail-header">
              <div className="dir-detail-avatar">{business.name.charAt(0)}</div>
              <div>
                <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
                  <span className={`directory-type-badge ${business.contentType}`}>
                    {business.contentType === 'business' ? '🏪' : business.contentType === 'blog' ? '✍️' : business.contentType === 'vlog' ? '📹' : '🎙'} {business.contentType}
                  </span>
                  {business.organicCertified && <span className="badge badge-spring">🌿 {business.certificationBody ?? 'Organic certified'}</span>}
                  {business.featured && <span className="text-caption" style={{ color: 'var(--ochre)' }}>⭐ Featured</span>}
                </div>
                <h1 className="text-headline">{business.name}</h1>
                <p className="text-subtitle" style={{ color: 'var(--sage)', marginBottom: 'var(--space-3)' }}>{business.tagline}</p>
                <p className="text-caption" style={{ color: 'var(--ink-faint)' }}>{coverageLabel(business)}</p>
              </div>
            </div>

            <div className="paper" style={{ padding: 'var(--space-6)', marginBottom: 'var(--space-5)' }}>
              <h2 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>About</h2>
              <p className="text-body" style={{ color: 'var(--ink-light)' }}>{business.description}</p>

              {business.services.length > 0 && (
                <div style={{ marginTop: 'var(--space-5)' }}>
                  <h3 className="text-label" style={{ marginBottom: 'var(--space-3)' }}>Services</h3>
                  <ul style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {business.services.map(s => (
                      <li key={s} className="text-caption" style={{ padding: 'var(--space-1) var(--space-3)', background: 'var(--sage-wash)', borderRadius: 'var(--radius-full)', color: 'var(--sage)' }}>{s}</li>
                    ))}
                  </ul>
                </div>
              )}

              {business.latestPostTitle && (
                <div style={{ marginTop: 'var(--space-5)', padding: 'var(--space-4)', background: 'var(--ochre-pale)', borderRadius: 'var(--radius)', borderLeft: '3px solid var(--ochre)' }}>
                  <p className="text-label" style={{ color: 'var(--ochre)', marginBottom: 'var(--space-1)' }}>Latest post</p>
                  <p className="text-hand">{business.latestPostTitle}</p>
                  {business.latestPostDate && <p className="text-caption" style={{ color: 'var(--ink-faint)', marginTop: '4px' }}>{new Date(business.latestPostDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                </div>
              )}
            </div>

            {/* Reviews */}
            <div className="paper" style={{ padding: 'var(--space-6)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-5)' }}>
                <div>
                  <h2 className="text-title">Reviews</h2>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                    <Stars rating={business.averageRating} />
                    <span className="text-hand">{business.averageRating.toFixed(1)}</span>
                    <span className="text-caption" style={{ color: 'var(--ink-faint)' }}>({business.reviewCount} review{business.reviewCount !== 1 ? 's' : ''})</span>
                  </div>
                </div>
                <button className="btn-sage" style={{ fontSize: '0.875rem' }} onClick={() => setShowReviewForm(!showReviewForm)}>
                  {showReviewForm ? 'Cancel' : '+ Write a review'}
                </button>
              </div>

              {showReviewForm && (
                <div className="review-form animate-fade-in">
                  <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
                    <label>Rating</label>
                    <div className="star-selector">
                      {[1,2,3,4,5].map(n => (
                        <button key={n} className={`star-btn ${reviewForm.rating >= n ? 'star-btn--active' : ''}`} onClick={() => setReviewForm({ ...reviewForm, rating: n })}>★</button>
                      ))}
                    </div>
                  </div>
                  <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
                    <label>Title</label>
                    <input className="input" placeholder="Summarise your experience" value={reviewForm.title} onChange={e => setReviewForm({ ...reviewForm, title: e.target.value })} />
                  </div>
                  <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
                    <label>Your review</label>
                    <textarea className="textarea" rows={4} placeholder="Tell other growers about your experience…" value={reviewForm.body} onChange={e => setReviewForm({ ...reviewForm, body: e.target.value })} />
                  </div>
                  <button className="btn-sage" disabled={!reviewForm.title || !reviewForm.body}>Submit review</button>
                </div>
              )}

              <div className="reviews-list">
                {business.reviews.map(review => (
                  <div key={review.id} className="review-item">
                    <div className="review-header">
                      <div className="review-author">
                        <span className="review-avatar">{review.userName.charAt(0)}</span>
                        <div>
                          <span className="text-hand">{review.userName}</span>
                          {review.verified && <span className="text-caption" style={{ color: 'var(--sage)', marginLeft: 'var(--space-2)' }}>✓ Verified</span>}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Stars rating={review.rating} />
                        <p className="text-caption" style={{ color: 'var(--ink-faint)' }}>{new Date(review.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <h4 className="text-hand" style={{ marginBottom: 'var(--space-2)' }}>{review.title}</h4>
                    <p className="text-small" style={{ color: 'var(--ink-light)', lineHeight: 1.7 }}>{review.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <aside className="directory-detail-sidebar">
            <div className="paper" style={{ padding: 'var(--space-5)' }}>
              <h3 className="text-title" style={{ marginBottom: 'var(--space-4)' }}>Contact</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {business.website && <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-small" style={{ color: 'var(--sage)' }}>🌐 Website</a>}
                {business.email && <a href={`mailto:${business.email}`} className="text-small" style={{ color: 'var(--sage)' }}>✉️ {business.email}</a>}
                {business.socialLinks?.instagram && <a href={`https://instagram.com/${business.socialLinks.instagram}`} target="_blank" rel="noopener noreferrer" className="text-small" style={{ color: 'var(--sage)' }}>📸 Instagram</a>}
                {business.socialLinks?.youtube && <a href={`https://youtube.com/${business.socialLinks.youtube}`} target="_blank" rel="noopener noreferrer" className="text-small" style={{ color: 'var(--sage)' }}>📹 YouTube</a>}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <span className="stars" aria-label={`${rating} out of 5 stars`}>
      {[1,2,3,4,5].map(n => (
        <span key={n} className={n <= rating ? 'star-filled' : 'star-empty'}>★</span>
      ))}
    </span>
  );
}
